import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  type PayloadAction,
} from "@reduxjs/toolkit";

import {
  CreateConversationMessagesThunk,
  DeleteConversationMessageFileThunk,
  DeleteConversationMessagesThunk,
  GetConversationMessagesThunk,
  GetConversationsThunk,
  GetConversationsUnreadThunk,
  HandleUndeliveredMessagesThunk,
  ReadConversationMessagesThunk,
  ResendConversationMessagesThunk,
  UpdateConversationMessagesThunk,
} from "../redux/thunk";
import { MessagesStatusesEnum } from "../constants";
import { differenceBy } from "lodash";
import type {
  ConversationReducer,
  UnreadConversationEvent,
  OnlineOfflineStatusEvent,
  IsTypingEvent,
  SelectedConversation,
  MessageAuthor,
  ConversationDetail,
  NewMessagesEvent,
  ReadMessagesEvent,
  ConversationMessageDetail,
  UpdateMessageEvent,
  DeleteMessageEvent,
  ConversationMessageShort,
} from "../ts";

export type ErrorsFromBackendType =
  | Record<string, string | Array<string>>
  | undefined;

const initialState: ConversationReducer = {
  unreadConversations: [],
  total_unread_message_count: 0,
  pagination: {
    limit: 0,
    offset: 0,
  },
  search: "",
  conversations: undefined,
  selected_conversation: null,
  error: undefined,
};

export const conversationsSlice = createSlice({
  name: "conversations",
  initialState: initialState,
  reducers: {
    increaseUnreadCount: (
      state,
      action: PayloadAction<UnreadConversationEvent>,
    ) => {
      if (!state.unreadConversations.includes(action.payload.conversation_id))
        state.unreadConversations = [
          ...state.unreadConversations,
          action.payload.conversation_id,
        ];
    },
    removeUnreadMessagesCount: (state, action) => {
      if (state.conversations && state.conversations.results) {
        state.conversations.results = state.conversations.results.map((conv) =>
          conv.id === action.payload.conversation_id
            ? { ...conv, unread_messages_count: 0 }
            : conv,
        );
      }
    },
    setSelectedConversation: (state, action) => {
      if (
        state.selected_conversation?.conversation.id !==
        action.payload.conversation.id
      ) {
        state.selected_conversation = action.payload;
      }
    },
    // Set online or offline status of a member in the conversation
    setMemberStatus: (
      state,
      action: PayloadAction<OnlineOfflineStatusEvent>,
    ) => {
      state.conversations = state.conversations
        ? {
            ...state.conversations,
            results: state.conversations.results.map((conversation) =>
              conversation.provider.id === action.payload.profile_id
                ? {
                    ...conversation,
                    provider: {
                      ...conversation.provider,
                      is_online: action.payload.is_online,
                    },
                  }
                : conversation.client.id === action.payload.profile_id
                  ? {
                      ...conversation,
                      client: {
                        ...conversation.client,
                        is_online: action.payload.is_online,
                      },
                    }
                  : conversation,
            ),
          }
        : undefined;
      state.selected_conversation =
        state.selected_conversation?.companion.id === action.payload.profile_id
          ? {
              ...state.selected_conversation,
              companion: {
                ...state.selected_conversation.companion,
                is_online: action.payload.is_online,
              },
            }
          : state.selected_conversation;
    },
    setIsTyping: (state, action: PayloadAction<IsTypingEvent>) => {
      const selectedConv = state.selected_conversation;
      if (selectedConv) {
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          companion: {
            ...state.selected_conversation?.companion,
            is_typing: action.payload.is_typing,
          } as MessageAuthor,
        };
      }
      const conversationObj = state.conversations?.results.find(
        (conv) => conv.id === action.payload.chat_id,
      );
      const updatedConvObj = {
        ...conversationObj,
        provider:
          conversationObj?.provider.id === action.payload.profile_id
            ? {
                ...conversationObj.provider,
                is_typing: action.payload.is_typing,
              }
            : conversationObj?.provider,
        client:
          conversationObj?.client.id === action.payload.profile_id
            ? { ...conversationObj.client, is_typing: action.payload.is_typing }
            : conversationObj?.client,
      } as ConversationDetail;
      state.conversations = state.conversations
        ? {
            ...state.conversations,
            results: state.conversations.results.map((conversation) =>
              conversation.id === action.payload.chat_id
                ? updatedConvObj
                : conversation,
            ),
          }
        : undefined;
    },
    clearState: () => initialState,
    clearConversationMessages: (state) => {
      state.selected_conversation = {
        ...(state.selected_conversation as SelectedConversation),
        messages: [],
      };
    },
    clearSelectedConversation: (state) => {
      state.selected_conversation = null;
    },
    addNewMessages: (state, action: PayloadAction<NewMessagesEvent>) => {
      const selectedConvObj = state.selected_conversation;
      const newMessages = action.payload.messages;
      if (selectedConvObj?.conversation?.id === action.payload.chat_id) {
        const convMessages = selectedConvObj.messages;

        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [...action.payload.messages, ...convMessages],
        };
      }
      const lastMessage = newMessages[newMessages.length - 1];
      state.conversations = state.conversations
        ? {
            ...state.conversations,
            results: state.conversations.results
              .map((conv) =>
                conv.id === action.payload.chat_id
                  ? {
                      ...conv,
                      last_message: {
                        ...lastMessage,
                        status: MessagesStatusesEnum.sent_index as number,
                        message:
                          lastMessage.message ?? lastMessage.message_preview,
                      },
                      unread_messages_count:
                        (conv.unread_messages_count > 0
                          ? conv.unread_messages_count
                          : 0) + newMessages.length,
                    }
                  : conv,
              )
              .sort(
                (a, b) =>
                  new Date(b.last_message?.created_at).getTime() -
                  new Date(a.last_message?.created_at).getTime(),
              ),
          }
        : undefined;
      state.total_unread_message_count = (state.total_unread_message_count ?? 0 + newMessages.length);
    },
    updateMessages: (state, action: PayloadAction<UpdateMessageEvent>) => {
      const { chat_id, messages } = action.payload;

      if (!messages?.length) return;

      const updatedMap = new Map(
        messages.map((m: ConversationMessageDetail) => [m.id, m]),
      );

      if (
        state.selected_conversation &&
        state.selected_conversation?.conversation?.id === chat_id
      ) {
        state.selected_conversation = {
          ...state.selected_conversation,
          conversation: {
            ...state.selected_conversation.conversation,
            last_message: updatedMap.has(
              state.selected_conversation.conversation.last_message.id,
            )
              ? ({} as ConversationMessageShort)
              : state.selected_conversation.conversation.last_message,
          },
          messages: state.selected_conversation.messages.map((msg) =>
            updatedMap.has(msg.id)
              ? { ...msg, ...updatedMap.get(msg.id) }
              : msg,
          ),
        };
      }

      if (state.conversations) {
        state.conversations = {
          ...state.conversations,
          results: state.conversations.results.map((conv) => {
            if (conv.id !== chat_id) return conv;
            if ((conv as ConversationDetail).messages) {
              return {
                ...conv,
                last_message: updatedMap.has(conv.last_message.id)
                  ? {
                      ...conv.last_message,
                      message: updatedMap.get(conv.last_message.id)
                        ?.message as string,
                    }
                  : conv.last_message,
                messages: (conv as ConversationDetail).messages.map(
                  (msg: ConversationMessageDetail) =>
                    updatedMap.has(msg.id)
                      ? { ...msg, ...updatedMap.get(msg.id) }
                      : msg,
                ),
              };
            }
            return conv;
          }),
        };
      }
    },
    deleteMessages: (state, action: PayloadAction<DeleteMessageEvent>) => {
      const { chat_id, message_id_list } = action.payload;
      if (!message_id_list?.length) return;

      const idSet = new Set(message_id_list);
      if (
        state.selected_conversation &&
        state.selected_conversation?.conversation?.id === chat_id
      ) {
        state.selected_conversation = {
          ...state.selected_conversation,
          conversation: {
            ...state.selected_conversation.conversation,
            last_message: idSet.has(
              state.selected_conversation.conversation.last_message.id,
            )
              ? ({} as ConversationMessageShort)
              : state.selected_conversation.conversation.last_message,
          },
          messages: state.selected_conversation.messages.filter(
            (msg) => !idSet.has(msg.id),
          ),
        };
      }

      if (state.conversations) {
        state.conversations = {
          ...state.conversations,
          results: state.conversations.results.map((conv) => {
            if (conv.id !== chat_id) return conv;
            if ((conv as ConversationDetail).messages) {
              return {
                ...conv,
                last_message: idSet.has(conv.last_message.id)
                  ? ({} as ConversationMessageShort)
                  : conv.last_message,
                messages: (conv as ConversationDetail).messages.filter(
                  (msg: ConversationMessageDetail) => !idSet.has(msg.id),
                ),
              };
            }
            return conv;
          }),
        };
      }
    },
    readMessages: (state, action: PayloadAction<ReadMessagesEvent>) => {
      const readMessagesIdList = action.payload.message_id_list;
      const convId = action.payload.chat_id;
      const selectedConvObj = state.selected_conversation;
      if (selectedConvObj?.conversation?.id === convId) {
        const convMessages = selectedConvObj.messages;
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: convMessages.map((mes) =>
            readMessagesIdList.includes(mes.id)
              ? { ...mes, status: MessagesStatusesEnum.read as string }
              : mes,
          ),
        };
      }
      state.conversations = state.conversations
        ? {
            ...state.conversations,
            results: state.conversations.results.map((conv) =>
              conv.id === convId
                ? {
                    ...conv,
                    last_message:
                      conv.last_message &&
                      conv.last_message.author &&
                      readMessagesIdList.includes(conv.last_message?.id)
                        ? {
                            ...conv.last_message,
                            status: MessagesStatusesEnum.read_index as number,
                          }
                        : conv.last_message,
                  }
                : conv,
            ),
          }
        : undefined;
    },
    setTotalUnreadMessageCount: (state, action) => {
      state.total_unread_message_count = action.payload.count;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isPending(GetConversationsThunk), () => {
      // state.is_loading = true;
    });
    builder.addMatcher(isRejected(GetConversationsThunk), () => {
      // state.is_loading = false;
      // state.error = action.payload as ErrorsFromBackendType;
    });
    builder.addMatcher(isFulfilled(GetConversationsThunk), (state, action) => {
      const { results, ...rest } = action.payload;
      if (!state.conversations || action.meta.arg.offset === 0) {
        state.conversations = action.payload;
      } else {
        state.conversations = {
          ...rest,
          results: [...state.conversations.results, ...results],
        };
      }

      const { limit, offset, search } = action.meta.arg;
      state.pagination.limit = limit;
      state.pagination.offset = offset;
      state.search = search;
      // state.is_loading = false;
      // state.error = undefined;
    });
    builder.addMatcher(isPending(GetConversationMessagesThunk), (state) => {
      state.selected_conversation = {
        ...(state.selected_conversation as SelectedConversation),
        isMessagesLoading: true,
      };
    });
    builder.addMatcher(
      isFulfilled(GetConversationMessagesThunk),
      (state, action) => {
        const existingMessages = state.selected_conversation?.messages
          ? state.selected_conversation.messages
          : [];
        const newMessages = differenceBy(
          action.payload.results,
          existingMessages,
          "id",
        );
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [...existingMessages, ...newMessages],
          next: action.payload.next,
          total: action.payload.count,
          isMessagesLoading: false,
          isMessagesLoaded: true,
          offset: action.meta.arg.offset,
          limit: action.meta.arg.limit,
        };
      },
    );
    builder.addMatcher(
      isRejected(GetConversationMessagesThunk),
      (state, action) => {
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          errors: action.payload as ErrorsFromBackendType,
          isMessagesLoading: false,
        };
      },
    );
    builder.addMatcher(
      isFulfilled(ReadConversationMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const readMessagesIdList = action.meta.arg.message_id_list;
        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) =>
                conv.id === action.meta.arg.conversation_id
                  ? {
                      ...conv,
                      unread_messages_count:
                        conv.unread_messages_count - readMessagesIdList.length >
                        0
                          ? conv.unread_messages_count -
                            readMessagesIdList.length
                          : 0,
                      last_message:
                        conv.last_message &&
                        readMessagesIdList.includes(conv.last_message?.id)
                          ? {
                              ...conv.last_message,
                              status: MessagesStatusesEnum.read_index as number,
                            }
                          : conv.last_message,
                    }
                  : conv,
              ),
            }
          : undefined;
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [...conversationMessages].map((mes) =>
            readMessagesIdList.includes(mes?.id)
              ? { ...mes, status: "Read" }
              : mes,
          ),
        };
      },
    );
    builder.addMatcher(
      isRejected(ReadConversationMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const readMessagesIdList = action.meta.arg.message_id_list;
        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) =>
                conv.id === action.meta.arg.conversation_id
                  ? {
                      ...conv,
                      unread_messages_count:
                        conv.unread_messages_count - readMessagesIdList.length,
                      last_message:
                        conv.last_message &&
                        readMessagesIdList.includes(conv.last_message?.id)
                          ? {
                              ...conv.last_message,
                              status: MessagesStatusesEnum.read_index as number,
                            }
                          : conv.last_message,
                    }
                  : conv,
              ),
            }
          : undefined;
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [...conversationMessages].map((mes) =>
            readMessagesIdList.includes(mes?.id)
              ? { ...mes, status: "Read" }
              : mes,
          ),
        };
      },
    );
    builder.addMatcher(
      isFulfilled(ResendConversationMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const readMessagesIdList = action.meta.arg.message_id_list;
        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) =>
                conv.id === action.meta.arg.conversation_id
                  ? {
                      ...conv,
                      last_message:
                        conv.last_message &&
                        readMessagesIdList.includes(conv.last_message?.id)
                          ? {
                              ...conv.last_message,
                              status: MessagesStatusesEnum.sent_index as number,
                            }
                          : conv.last_message,
                    }
                  : conv,
              ),
            }
          : undefined;
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [...conversationMessages].map((mes) =>
            readMessagesIdList.includes(mes?.id)
              ? { ...mes, status: MessagesStatusesEnum.sent as string }
              : mes,
          ),
        };
      },
    );
    builder.addMatcher(
      isPending(CreateConversationMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const newMessages = action.meta.arg.messages?.map((mes) => {
          const messageFiles = mes.files
            ? [...mes.files].map(() => ({
                id: mes.id,
                created_at: mes.created_at,
                // file: URL.createObjectURL(file),
              }))
            : undefined;
          return { ...mes, files: messageFiles } as ConversationMessageDetail;
        });

        const lastMessage =
          newMessages?.[action.meta.arg?.messages?.length ?? 0 - 1];
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [
            ...(newMessages as ConversationMessageDetail[]),
            ...conversationMessages,
          ],
        };
        if (lastMessage && state.conversations && state.conversations.results) {
          state.conversations.results = state.conversations.results.map(
            (conv) =>
              conv.id === action.meta.arg.conversation_id
                ? {
                    ...conv,
                    last_message: {
                      ...lastMessage,
                      status: MessagesStatusesEnum.undelivered_index as number,
                      message: lastMessage.message.length
                        ? lastMessage.message
                        : `${lastMessage.files?.length} file${
                            lastMessage.files?.length &&
                            lastMessage.files?.length > 1
                              ? "s"
                              : ""
                          } attached`,
                    },
                  }
                : conv,
          );
        }
      },
    );
    builder.addMatcher(
      isFulfilled(CreateConversationMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const lastMessage = action.payload[action.payload.length - 1];
        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: [
            ...action.payload,
            ...conversationMessages.filter(
              (mes) => mes.status !== MessagesStatusesEnum.undelivered,
            ),
          ],
        };
        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) =>
                conv.id === action.meta.arg.conversation_id
                  ? {
                      ...conv,
                      last_message: {
                        ...lastMessage,
                        status: MessagesStatusesEnum.sent_index as number,
                        message:
                          lastMessage.message ??
                          `${lastMessage.files?.length} file${
                            lastMessage.files?.length &&
                            lastMessage.files?.length > 1
                              ? "s"
                              : ""
                          } attached`,
                      },
                    }
                  : conv,
              ),
            }
          : undefined;
      },
    );
    builder.addMatcher(
      isFulfilled(GetConversationsUnreadThunk),
      (state, action) => {
        state.unreadConversations = action.payload.unread_conversations;
      },
    );
    builder.addMatcher(
      isFulfilled(HandleUndeliveredMessagesThunk),
      (state, action) => {
        const conversationMessages =
          state.selected_conversation?.messages ?? [];
        const lastMessage = action.payload[action.payload.length - 1];
        if (state.selected_conversation?.messages) {
          state.selected_conversation.messages = [
            ...action.payload,
            ...conversationMessages.filter(
              (mes) => mes.status !== MessagesStatusesEnum.undelivered,
            ),
          ];
        }
        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) =>
                conv.id === action.meta.arg.conversation_id
                  ? {
                      ...conv,
                      last_message: {
                        ...lastMessage,
                        status: MessagesStatusesEnum.sent_index as number,
                        message:
                          lastMessage.message ??
                          `${lastMessage.files?.length} file${
                            lastMessage.files?.length &&
                            lastMessage.files?.length > 1
                              ? "s"
                              : ""
                          } attached`,
                      },
                    }
                  : conv,
              ),
            }
          : undefined;
      },
    );
    builder.addMatcher(
      isFulfilled(DeleteConversationMessagesThunk),
      (state, action) => {
        const deletedMessages = action.payload;
        const conversationMessages =
          state.selected_conversation?.messages ?? [];

        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: conversationMessages.map((message) => {
            const deletedMatch = deletedMessages.find(
              (deleted) => deleted.id === message.id,
            );
            return deletedMatch ? deletedMatch : message;
          }),
        };

        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) => {
                if (conv.id !== action.meta.arg.conversation_id) return conv;

                const deletedIds = deletedMessages.map((m) => m.id);
                const isLastMessageDeleted = deletedIds.includes(
                  conv.last_message?.id,
                );

                if (!isLastMessageDeleted) return conv;

                return {
                  ...conv,
                  last_message: conv.last_message
                    ? {
                        ...conv.last_message,
                        message: "",
                        id: conv.last_message.id,
                        status: conv.last_message.status,
                        created_at: conv.last_message.created_at,
                        author: conv.last_message.author,
                      }
                    : {
                        message: "",
                        id: "",
                        status: 0,
                        created_at: "",
                        author: undefined,
                      },
                };
              }),
            }
          : undefined;
      },
    );
    builder.addMatcher(
      isFulfilled(UpdateConversationMessagesThunk),
      (state, action) => {
        const updatedMessages = action.payload;
        const conversationMessages =
          state.selected_conversation?.messages ?? [];

        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: conversationMessages.map((message) => {
            const updatedMatch = updatedMessages.find(
              (deleted) => deleted.id === message.id,
            );
            return updatedMatch ? updatedMatch : message;
          }),
        };

        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) => {
                if (conv.id !== action.meta.arg.conversation_id) return conv;

                const updatedMessage = updatedMessages.find(
                  (m) => m.id === conv.last_message.id,
                );

                if (!updatedMessage) return conv;

                return {
                  ...conv,
                  last_message: conv.last_message
                    ? {
                        ...conv.last_message,
                        message: updatedMessage.message,
                        id: conv.last_message.id,
                        status: conv.last_message.status,
                        created_at: conv.last_message.created_at,
                        author: conv.last_message.author,
                      }
                    : {
                        message: "",
                        id: "",
                        status: 0,
                        created_at: "",
                        author: undefined,
                      },
                };
              }),
            }
          : undefined;
      },
    );
    builder.addMatcher(
      isFulfilled(DeleteConversationMessageFileThunk),
      (state, action) => {
        const updatedMessage = action.payload;
        const conversationMessages =
          state.selected_conversation?.messages ?? [];

        state.selected_conversation = {
          ...(state.selected_conversation as SelectedConversation),
          messages: conversationMessages.map((message) => {
            return message.id === updatedMessage.id ? updatedMessage : message;
          }),
        };

        state.conversations = state.conversations
          ? {
              ...state.conversations,
              results: state.conversations.results.map((conv) => {
                if (conv.id !== action.meta.arg.conversation_id) return conv;

                if (updatedMessage.id !== conv.last_message.id) return conv;

                return {
                  ...conv,
                  last_message: conv.last_message
                    ? {
                        ...conv.last_message,
                        message: "",
                        id: conv.last_message.id,
                        status: conv.last_message.status,
                        created_at: conv.last_message.created_at,
                        author: conv.last_message.author,
                      }
                    : {
                        message: "",
                        id: "",
                        status: 0,
                        created_at: "",
                        author: undefined,
                      },
                };
              }),
            }
          : undefined;
      },
    );
  },
});

export const conversationsActions = conversationsSlice.actions;

export default conversationsSlice.reducer;
